// packages/whiteboard/src/features/line/line-options.config.ts

import {
  BORDER_ACTIONS,
  BORDER_WIDTH_ACTIONS,
  BORDER_STYLE_ACTIONS,
  BOX_SHADOW_ACTIONS,
  LINE_CAP_ACTIONS,
  LINE_SWAP_ACTIONS,
  LINE_PATH_ACTIONS,
  Z_ORDER_ACTIONS,
  ALIGN_ACTIONS,
  BOARD_COMMAND_ACTIONS,
  type ActionIntent,
  type OptionsCircleDef,
} from '../../optionsbar/optionsbar-actions.js';
import { composeOptionsCircle } from '../../optionsbar/parts/compose-options-circle.js';

// Optionskreis aus Einzelteilen (Linie = 1+2+7): Standard-Knöpfe +
// Linienfarbe/-stärke/-art/Schatten + Linienenden/Tauschen/Führung.
const lineSvg = composeOptionsCircle([1, 2, 7]);

/**
 * Line-specific optionsbar mapping (PRD-F5 §TR16 / Task T26). The slice owns
 * the contract between the generic optionsbar `ActionIntent` and the
 * `LineObject` property names, mirroring the sticky / checkbox / person
 * patterns from PRD-E / PRD-F2.
 *
 * The `border-*` intents read as "stroke" for a line: the SVG's line-colour
 * and line-width widgets are the shared border ones.
 *
 * `pathStrategy` is reserved for a future strategy-selector UI (Bezier /
 * Orthogonal) and remains a stub (`null`) until that picker is implemented.
 */

/** Resolves an optionsbar action intent to a LineObject property. */
export function resolveLineProperty(intent: ActionIntent): string | null {
  switch (intent) {
    case 'border-color':
      return 'strokeColor';
    case 'border-width':
      return 'strokeWidth';
    case 'border-style':
      return 'strokeStyle';
    case 'box-shadow':
      return 'boxShadow';
    // The SVG labels the ends left/right; the model calls them start/end.
    case 'line-cap-start':
      return 'startCap';
    case 'line-cap-end':
      return 'endCap';
    // 'line-swap-caps' is intentionally absent: swapping touches four fields
    // plus both anchors and runs as a command, not a property write.
    default:
      return null;
  }
}

/**
 * Line options circle. Composed entirely from shared building blocks — no
 * bespoke action logic.
 *
 * Two groups the SVG draws are deliberately NOT wired, because the model
 * cannot honour them:
 *  - `action-border-radius-*`: only the `straight` path strategy exists, and a
 *    straight line has no corners to round. (part2 draws them; inert here.)
 *  - `action-border-width-0`: part2 draws a stroke-width-0 button, but width 0
 *    would make the line invisible rather than "borderless". We drop it from the
 *    line's wiring, so the drawn button stays an inert graphic.
 */
const { 'action-border-width-0': _omitWidthZero, ...LINE_BORDER_WIDTH_ACTIONS } =
  BORDER_WIDTH_ACTIONS;

export const LINE_OPTIONS: OptionsCircleDef = {
  svg: lineSvg,
  actions: {
    ...BORDER_ACTIONS,
    ...LINE_BORDER_WIDTH_ACTIONS,
    ...BORDER_STYLE_ACTIONS,
    ...BOX_SHADOW_ACTIONS,
    ...LINE_CAP_ACTIONS,
    ...LINE_SWAP_ACTIONS,
    ...LINE_PATH_ACTIONS,
    ...Z_ORDER_ACTIONS,
    ...ALIGN_ACTIONS,
    ...BOARD_COMMAND_ACTIONS,
  },
  resolveProperty: resolveLineProperty,
};
